import { useContext, useRef } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import { parseUIAiConfigFilterFromBrowser } from '@gonfalon/ai-configs/filters';
import { useEventLocation } from '@gonfalon/analytics/useEventLocation';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { parseUIContextsViewFromBrowser } from '@gonfalon/contexts/kinds/parseUIContextsViewFromBrowser';
import { serializeUIContextsViewForAPI } from '@gonfalon/contexts/kinds/serializeUIContextsViewForAPI';
import { parseUIFlagListViewFromBrowser } from '@gonfalon/flag-filters/parseUIFlagListViewFromBrowser';
import { serializeUIFlagListViewForAPI } from '@gonfalon/flag-filters/serializeUIFlagListViewForAPI';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useParam } from '@gonfalon/router/useParam';
import { parseUISegmentsViewFromBrowser } from '@gonfalon/segments/parseUISegmentsViewFromBrowser';
import { serializeUISegmentsViewForAPI } from '@gonfalon/segments/serializeUISegmentsViewForAPI';
import { convertToKey } from '@gonfalon/strings/convertToKey';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ButtonGroup,
  FieldError,
  FieldGroup,
  Form,
  Input,
  Label,
  RadioGroup,
  RadioIconButton,
  TextField,
} from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { z } from 'zod';

import { trackShortcutCreated } from '../../analytics';
import type { Shortcut } from '../../types';
import { useShortcutData } from '../../useShortcutData';

import styles from './ShortcutsForm.module.css';

export const ShortcutsIcons: IconName[] = [
  'bolt',
  'toggle-bolt',
  'package-outline-bolt',
  'chart-venn-bolt',
  'flask-bolt',
  'ruler-bolt',
  'fingerprint-bolt',
  'flag-outline',
  'toggle-on',
  'flask',
  'fingerprint',
  'ruler',
  'arrow-connect',
  'chart-venn',
  'book-outline',
  'map',
  'package-outline',
  'shield-key',
  'shield-heart-outline',
  'approval-pending',
  'approval-approved',
  'approval-denied',
  'approval-applied',
  'animal-cat',
  'animal-turtle',
  'osmo',
  'broom',
  'stack',
];

export type ShortcutsFormValues = {
  name: string;
  icon: string;
};

interface UseShortcutsFormOptions {
  shortcutType: Shortcut['type'];
  /**
   * Called after a shortcut is created. The dialog leaves this unset
   * and falls back to the overlay's own `close()`; the command-palette
   * view passes its `close()` here because it has no `DialogTrigger`
   * overlay state to read.
   */
  onSubmitSuccess?: () => void;
}

export function useShortcutsForm({ shortcutType, onSubmitSuccess }: UseShortcutsFormOptions) {
  const { context } = useProjectContext();
  const { createShortcut, shortcuts } = useShortcutData({ projectKey: context.projectKey });
  const [searchParams] = useSearchParams();
  const location = useEventLocation();
  const flagKey = useParam('flagKey', { optional: true });
  const viewKey = useParam('viewKey', { optional: true });
  const aiConfigKey = useParam('configKey', { optional: true });
  const state = useContext(OverlayTriggerStateContext);
  const submissionInFlight = useRef(false);

  const isUniqueKey = (name: string) =>
    !shortcuts.map((shortcut: Shortcut) => shortcut.key).includes(convertToKey(name));

  const shortcutSchema = z.object({
    name: z
      .string()
      .nonempty({ message: 'Shortcut name is required' })
      .max(256, 'Shortcut name must be less then 256 characters')
      .refine(isUniqueKey, 'Shortcut name must be unique'),
    icon: z.string().nonempty(),
  });

  const {
    formState: { isSubmitting, isValid },
    handleSubmit,
    control,
  } = useForm<ShortcutsFormValues>({
    resolver: zodResolver(shortcutSchema),
    mode: 'onChange',
    defaultValues: {
      icon: ShortcutsIcons[0],
    },
  });

  const onSubmit = async ({ name, icon }: ShortcutsFormValues) => {
    let filters;
    let entityKey;

    switch (shortcutType) {
      case 'flag':
        entityKey = flagKey;
        break;
      case 'flags':
        filters = serializeUIFlagListViewForAPI(parseUIFlagListViewFromBrowser(searchParams));
        break;
      case 'contexts':
        filters = serializeUIContextsViewForAPI(parseUIContextsViewFromBrowser(searchParams));
        break;
      case 'segments':
        filters = serializeUISegmentsViewForAPI(parseUISegmentsViewFromBrowser(searchParams));
        break;
      case 'view':
        entityKey = viewKey;
        break;
      case 'ai-configs':
        filters = parseUIAiConfigFilterFromBrowser(searchParams);
        break;
      case 'ai-config':
        entityKey = aiConfigKey;
        break;
      default:
        break;
    }

    const nextShortcut = {
      name,
      key: convertToKey(name),
      icon,
      type: shortcutType,
      context,
      filters,
      visibility: 'me', // TODO: implement visibility
      entityKey,
    } as Shortcut;

    trackShortcutCreated({
      type: shortcutType,
      name,
      location,
    });
    // Close only once the create actually succeeds. Closing eagerly
    // (before the mutation resolves) would dismiss the palette view
    // while the request is still in flight, so a later failure would
    // surface its error toast with the form already gone -- forcing the
    // user to reopen to retry. On error the mutation shows a toast and
    // we leave the form open.
    await createShortcut(nextShortcut, {
      onSuccess: () => {
        if (onSubmitSuccess) {
          onSubmitSuccess();
        } else {
          state?.close();
        }
      },
    });
  };

  const handleSubmitOnce = handleSubmit(onSubmit);
  const submit: typeof handleSubmitOnce = async (event) => {
    if (submissionInFlight.current) {
      event?.preventDefault();
      return;
    }
    submissionInFlight.current = true;
    try {
      await handleSubmitOnce(event);
    } finally {
      submissionInFlight.current = false;
    }
  };

  return {
    control,
    isSubmitting,
    isValid,
    submit,
  };
}

export type ShortcutsFormController = ReturnType<typeof useShortcutsForm>;

export function ShortcutsFormFields({ form }: { form: ShortcutsFormController }) {
  return (
    <Form id="shortcut" onSubmit={form.submit}>
      <FieldGroup className={styles.form}>
        <Controller
          control={form.control}
          name="name"
          render={({ field: { name, value, onChange, onBlur, ref }, fieldState: { invalid, error } }) => (
            <TextField
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              validationBehavior="aria"
              isInvalid={invalid}
            >
              <Label className={styles.label}>
                <Message id="shortcuts.saveForm.nameLabel" />
              </Label>
              <Input
                data-1p-ignore
                autoFocus
                autoComplete="off"
                ref={ref}
                placeholder={msg('shortcuts.saveForm.namePlaceholder')}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={form.control}
          name="icon"
          render={({ field: { name, value, onChange, onBlur }, fieldState: { invalid } }) => (
            <RadioGroup
              name={name}
              value={value}
              onBlur={onBlur}
              validationBehavior="aria"
              isInvalid={invalid}
              onChange={onChange}
            >
              <Label>
                <Message id="shortcuts.saveForm.iconLabel" />
              </Label>
              <ButtonGroup className={styles.iconGroup} role="presentation" spacing="basic">
                {ShortcutsIcons.map((icon) => (
                  <RadioIconButton icon={icon} size="medium" key={icon} value={icon} aria-label={icon} />
                ))}
              </ButtonGroup>
            </RadioGroup>
          )}
        />
      </FieldGroup>
    </Form>
  );
}

export function ShortcutsForm(options: UseShortcutsFormOptions) {
  const form = useShortcutsForm(options);
  return <ShortcutsFormFields form={form} />;
}
